/**
 * Resolve a VIP rank's display label. Returns the operator-defined custom
 * name if non-empty, otherwise falls back to "VIP <n>" for the VIP ladder.
 *
 * @param {object} rank    Rank object from getVipRankDetails with { customDisplayName?, vipRank }
 * @param {string} fallback  Optional label to use when `rank` is missing entirely
 */
export const getVipRankLabel = (rank, fallback) => {
  const custom = rank?.customDisplayName?.trim();
  if (custom) return custom;
  if (rank?.vipRank !== undefined && rank?.vipRank !== null) {
    return `VIP ${rank.vipRank}`;
  }
  return fallback ?? '';
};

/**
 * Look up a rank record by its numeric rank number in the getVipRankDetails
 * response array.
 *
 * @param {Array}  vipRanks    Array from getVipRankDetails result
 * @param {number} rankNumber  Numeric rank (0..N)
 */
export const findRankByNumber = (vipRanks, rankNumber) =>
  (vipRanks || []).find((r) => Number(r.vipRank) === Number(rankNumber));

/**
 * Given the player's current rank and total accumulated deposit, walk the
 * ladder and compute the highest rank a single Upgrade-button click would
 * land the player at (Option A: cross the first manual gate, then auto-cascade
 * through subsequent auto ranks until the next manual gate or top of ladder).
 *
 * Returns null when no eligible manual gate exists above the player's current
 * rank — in that case the button should not be shown.
 *
 * @param {string} currentPlayerRank  enum name (e.g. "VIP3", "BRONZE1")
 * @param {number|string} totalDeposit  player's total accumulated deposit
 * @param {Array} vipRanks  rank list from getVipRankDetails result
 */
export const findEligibleUpgrade = (currentPlayerRank, totalDeposit, vipRanks) => {
  if (!vipRanks?.length || currentPlayerRank == null) return null;
  const currentRankNum = parseInt(String(currentPlayerRank).replace(/\D/g, ''), 10);
  const deposit = Number(totalDeposit || 0);
  const sorted = [...vipRanks]
    .filter((r) => Number(r.vipRank) > currentRankNum)
    .sort((a, b) => Number(a.vipRank) - Number(b.vipRank));

  let firstGateCrossed = false;
  let target = null;
  for (const r of sorted) {
    if (r.accumulatedDeposit == null) break;
    if (deposit < Number(r.accumulatedDeposit)) break; // not eligible for this or any later
    if (r.manualUpgrade) {
      if (!firstGateCrossed) {
        firstGateCrossed = true;
        target = r;
        continue; // cascade continues past this gate
      }
      break; // second manual gate stops the click's cascade
    }
    // auto rank after the first gate: keep cascading
    if (firstGateCrossed) {
      target = r;
    }
    // else: an auto rank BELOW the first manual gate — the deposit path
    // already handled it; skip.
  }
  return firstGateCrossed ? target : null;
};
